import { Button, message, Popover, Select, Space, Table } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { appUiActions } from "../../../../config/actions/app/appUi.actions";
import permissionsThunks from "../../../../config/thunks/settings/permissions.thunks";
import { permissionsActions } from "../../../../config/actions/settings/permissions.actions";
import tableButtonActions from "../../../shared/Tables/TableButtonActions";
import TableTitle from "../../../shared/Tables/TableTitle";

const formatLabel = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());
const moduleFeatures = {
  administration: [
    "Stores_Register",
    "Staff_Records",
    "Manuals_and_policies",
    "Asset_register",
    "Asset_types",
    "Child_incident_protection_form",
    "Staff_Contracts",
    "Staff_Appraisals",
    "Staff_Minutes",
    "Leave_Applications",
    "Payroll",
  ],
  bunsinessDevelopmentAndPartnerships: ["Donors", "Projects"],
  finance: [
    "Chart_of_accounts",
    "Budgets",
    "Payment_voucher",
    "Advanced_accountability_form",
    "Requisition_memo",
    "Accounts",
    "Cashbook",
    "Trial_Balance",
    "Income_Statement",
    "Balance_Sheet",
  ],
  procurement: [
    "Request_For_Quotation",
    "Receiving_of_bids",
    "Bids_evaluation",
    "Notice_of_best_bidder",
    "Local_purchase_order",
  ],
  setting: ["Roles", "Permissions", "Log_details"],
  programmes: ["Activity_Concept_Notes"],
};
const featureModules = Object.entries(moduleFeatures).reduce(
  (acc, [module, features]) => {
    features.forEach((feature) => {
      acc[feature.toLowerCase()] = module;
    });
    return acc;
  },
  {}
);
const roleFromName = (record) => {
  const name = String(record.name || "");
  const suffix = ` ${record.function_id} PERMISSIONS`.toUpperCase();
  return name.toUpperCase().endsWith(suffix)
    ? name.slice(0, name.length - suffix.length)
    : undefined;
};
const uniqueOptions = (records, key) =>
  [...new Set(records.map((r) => r[key]).filter(Boolean))]
    .sort()
    .map((value) => ({ value, label: formatLabel(value) }));

const PermissionsTable = ({
  handleDeletePermissions,
  handleViewPermissionDetails,
}) => {
  const [deleteId, setDeleteId] = useState("");
  const [filterTable, setfilterTable] = useState(null);

  const deleteLoading = useSelector(
    (state) => state.permissionsState.deletePermission.loading
  );
  const {
    loading,
    data: rawData,
    error,
    success,
  } = useSelector((state) => state.permissionsState.permissions);
  const roles = useSelector((state) => state.permissionsState.roles);

  const [filters, setFilters] = useState({
    role: undefined,
    module: undefined,
    feature: undefined,
    access: [],
  });

  const dispatch = useDispatch();

  useEffect(() => {
    if (!roles.success) dispatch(permissionsThunks.getRoles());
  }, [dispatch, roles.success]);

  const data = useMemo(() => {
    const roleNames = Object.fromEntries(
      (roles.data || []).map((r) => [r.id, r.role])
    );
    return (Array.isArray(rawData) ? rawData : []).map((r) => ({
      ...r,
      role: r.role || roleNames[r.role_id] || roleFromName(r),
      module:
        r.module ||
        featureModules[String(r.function_id).toLowerCase()] ||
        "other",
    }));
  }, [rawData, roles.data]);

  const roleOptions = useMemo(() => uniqueOptions(data, "role"), [data]);
  const moduleOptions = useMemo(() => uniqueOptions(data, "module"), [data]);
  const featureOptions = useMemo(
    () =>
      uniqueOptions(
        filters.module ? data.filter((r) => r.module === filters.module) : data,
        "function_id"
      ),
    [data, filters.module]
  );
  const accessOptions = [
    { value: "create_record", label: "Create" },
    { value: "read", label: "Read" },
    { value: "update", label: "Update" },
    { value: "delete", label: "Delete" },
    { value: "check_record", label: "Check" },
    { value: "approve", label: "Approve" },
  ];

  const updateFilter = (key, value) =>
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      ...(key === "module" ? { feature: undefined } : {}),
    }));
  const clearFilters = () =>
    setFilters({
      role: undefined,
      module: undefined,
      feature: undefined,
      access: [],
    });
  const hasFilters =
    filters.role || filters.module || filters.feature || filters.access.length;

  const tableData = useMemo(
    () =>
      (filterTable === null ? data : filterTable).filter(
        (r) =>
          (!filters.role || r.role === filters.role) &&
          (!filters.module || r.module === filters.module) &&
          (!filters.feature || r.function_id === filters.feature) &&
          filters.access.every((key) => r[key] === true)
      ),
    [data, filterTable, filters]
  );

  const search = (value) => {
    const filterTable = data.filter((o) =>
      Object.keys(o).some((k) =>
        String(o[k]).toLowerCase().includes(value.toLowerCase())
      )
    );
    setfilterTable(filterTable);
  };
  const deleteRecord = (e) => {
    const newPermissionsArray = data.filter((ev) => ev.id === e.target.id);
    if (newPermissionsArray[0].name === "ADMIN PERMISSIONS PERMISSIONS")
      message.info("You cannot delete this permission");
    else setDeleteId(e.target.id);
  };
  const confirmDelete = () => {
    handleDeletePermissions(deleteId);
  };
  const updateRecord = (e) => {
    const updateRecordArray = data.filter((r) => r.id === e.target.id);
    dispatch(permissionsActions.setUpdatePermissionsData(updateRecordArray[0]));
    dispatch(appUiActions.toggleEditPermissionsModal(true));
  };
  const setfilterTableNull = (e) => {
    if (!e.target.value) setfilterTable(null);
  };
  const openAddModal = () => {
    dispatch(appUiActions.toggleAddPermissionsModal(true));
  };
  const exportRecords = () => message.info("Export Unavailable");
  const viewDetails = (e) => {
    handleViewPermissionDetails(e.target.id);
    dispatch(appUiActions.togglePermissionDetails(true));
  };
  const refreshTable = () => {
    dispatch(permissionsThunks.getPermissions());
    if (error && !success) message.error(error);
  };
  const showTotal = (total) => `Total: ${total}`;

  const columns = [
    {
      title: "Permission Name",
      key: "function",
      render: (text) => (
        <div>
          <span className="d-md-none">Permission: </span>
          {text.name} <br />
          <div className="d-md-none text-muted">
            {text.role} &middot; {formatLabel(text.function_id)}
          </div>
          <div className="d-md-none">
            <Popover
              className=""
              trigger="click"
              placement="bottom"
              content={() =>
                tableButtonActions(text, {
                  viewDetails,
                  updateRecord,
                  confirmDelete,
                  deleteRecord,
                  deleteLoading,
                  deleteId,
                })
              }
            >
              Actions: <Button type="link">Actions</Button>
            </Popover>
          </div>
        </div>
      ),
    },
    {
      title: "Role",
      key: "role",
      responsive: ["md"],
      sorter: (a, b) => String(a.role).localeCompare(String(b.role)),
      render: (text) => text.role,
    },
    {
      title: "Feature",
      key: "function_id",
      responsive: ["md"],
      sorter: (a, b) =>
        String(a.function_id).localeCompare(String(b.function_id)),
      render: (text) => formatLabel(text.function_id),
    },
    {
      title: "Action",
      key: "operation",
      responsive: ["md"],
      render: (text) => (
        <Popover
          trigger="click"
          placement="bottom"
          content={() =>
            tableButtonActions(text, {
              viewDetails,
              updateRecord,
              confirmDelete,
              deleteRecord,
              deleteLoading,
              deleteId,
            })
          }
        >
          <Button>Actions</Button>
        </Popover>
      ),
    },
  ];

  return (
    <div className="">
      <Space wrap className="mb-2">
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Filter by role"
          style={{ minWidth: 220 }}
          options={roleOptions}
          value={filters.role}
          onChange={(value) => updateFilter("role", value)}
        />
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Filter by module"
          style={{ minWidth: 180 }}
          options={moduleOptions}
          value={filters.module}
          onChange={(value) => updateFilter("module", value)}
        />
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Filter by feature"
          style={{ minWidth: 200 }}
          options={featureOptions}
          value={filters.feature}
          onChange={(value) => updateFilter("feature", value)}
        />
        <Select
          mode="multiple"
          allowClear
          placeholder="Has access to"
          style={{ minWidth: 200 }}
          options={accessOptions}
          value={filters.access}
          onChange={(value) => updateFilter("access", value)}
        />
        {hasFilters ? (
          <Button type="link" onClick={clearFilters}>
            Clear filters
          </Button>
        ) : null}
      </Space>
      <Table
        rowKey="id"
        pagination={{
          total: tableData.length,
          showSizeChanger: true,
          showTotal,
        }}
        loading={loading}
        title={() =>
          TableTitle({
            search,
            setfilterTableNull,
            refreshTable,
            exportRecords,
            openAddModal,
          })
        }
        bordered={true}
        dataSource={tableData}
        columns={columns}
      />
    </div>
  );
};

export default PermissionsTable;
